import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageShell } from "../components/Layout";
import api from "../lib/api";
import { Sparkle, MapPin, ChatCircleDots, Rocket, Compass, Warning, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import PaywallGate from "../components/PaywallGate";
import ToolAstrologerCta from "../components/ToolAstrologerCta";
import { useSavedReportLoader } from "../lib/useSavedReport";
import { useAuth } from "../context/AuthContext";
import useBirthDetailsPrefill from "../hooks/useBirthDetailsPrefill";

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
const MINUTES = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, "0"));

// ---- House-by-house wealth-alignment guidance for RAHU --------------------------------
const RAHU_HOUSE_GUIDES = {
  1: {
    theme: "Self · Identity · Face value",
    headline: "You ARE the brand. Wealth follows visibility.",
    pillars: [
      { icon: "👤", title: "Build a personal brand", detail: "Influencer, public-facing entrepreneur, fashion / fitness / beauty / motivational coach. Your face and presence is your highest-value asset." },
      { icon: "🎤", title: "Public speaking & visibility", detail: "TEDx-style stages, panels, conferences — every appearance increases your market price." },
      { icon: "📸", title: "Camera / on-screen work", detail: "Modelling, acting, anchoring, talk shows, brand campaigns. Get comfortable being SEEN." },
    ],
    avoid: "Going invisible in a corporate cubicle. You will under-earn dramatically if you hide.",
    energy: "When Rahu's dasha hits, your fame and income spike together. Lean in.",
  },
  2: {
    theme: "Wealth · Speech · Family · Resources",
    headline: "Talk-to-earn. Voice and resources are your gold mines.",
    pillars: [
      { icon: "🎙️", title: "Voice-based earning", detail: "YouTube, podcasting, voice-overs, language teaching, online courses. Anything that monetises your speech." },
      { icon: "🏦", title: "Banking, finance, jewellery", detail: "Stockbroking, bullion, gold trading, fintech, family-business banking — 2nd house wealth karma plays here." },
      { icon: "🍽️", title: "Food & F&B businesses", detail: "Cloud kitchens, premium restaurants, food brands. Rahu-2 loves modern food businesses with viral marketing." },
    ],
    avoid: "Hiding your voice. Speaking less than you earn from — Rahu in 2 punishes silence.",
    energy: "Tendency to overspend on luxury / status items. Save aggressively in early dasha years.",
  },
  3: {
    theme: "Self-effort · Communication · Short journeys · Hustle",
    headline: "Hustle wealth. Social media, content & sales is your zone.",
    pillars: [
      { icon: "📱", title: "Social media & short-form content", detail: "Instagram, YouTube Shorts, TikTok / Reels — Rahu in 3 is built for the viral-content gold rush." },
      { icon: "✍️", title: "Writing & publishing", detail: "Newsletters, blogs, books, screenplays. Your communication compounds into long-term royalties." },
      { icon: "💼", title: "Sales, marketing, courier, logistics", detail: "Anything that requires hustle, communication and short-distance movement. Founders / sales-led businesses thrive." },
    ],
    avoid: "Spreading yourself across 10 hustles. Pick 1-2 and double-down — Rahu in 3 fails when scattered.",
    energy: "Sibling / cousin partnerships are favoured for joint ventures.",
  },
  4: {
    theme: "Home · Property · Mother · Comfort",
    headline: "Property and comfort industries — including FOREIGN real estate — are your wealth engine.",
    pillars: [
      { icon: "🏠", title: "Real estate & property development", detail: "Domestic + NRI property markets. Rahu in 4 builds wealth through land, plots, rental income, REITs." },
      { icon: "🛋️", title: "Interiors, hospitality, automobiles", detail: "Anything that improves people's comfort. Hotels, premium car dealerships, luxury interiors, comfort tech." },
      { icon: "🌏", title: "Foreign-land / NRI wealth", detail: "Settling abroad, foreign property purchases, cross-border real estate. Classical Rahu-4 signature." },
    ],
    avoid: "Trying to find peace at home. Rahu here creates restlessness — accept it and channel the restlessness into property deals.",
    energy: "Buy land / property in your good dashas — it compounds silently for decades.",
  },
  5: {
    theme: "Speculation · Creativity · Children · Stocks",
    headline: "Speculation + creative output. Markets, crypto, entertainment.",
    pillars: [
      { icon: "📈", title: "Stock market & crypto", detail: "Rahu in 5 is the classical speculator. With discipline, you can mint extraordinary returns from markets." },
      { icon: "🎬", title: "Entertainment industry", detail: "Film, OTT, music, gaming, content production. Massive earners come from this placement." },
      { icon: "🧠", title: "Edu-tech & kids' products", detail: "Online courses, kids' brands, gamified education. Combines creativity with scale." },
    ],
    avoid: "Gambling without a system. Rahu-5 burns fortunes faster than it builds them when undisciplined.",
    energy: "Use 60/40 rule: 60% in stable instruments, 40% in speculative bets — never the reverse.",
  },
  6: {
    theme: "Competition · Service · Health · Enemies",
    headline: "You DEFEAT competitors and solve problems for money.",
    pillars: [
      { icon: "⚔️", title: "Service industry & competitive sectors", detail: "Defence, police, law, debt-recovery, competitive sports, B2B services. Rahu in 6 thrives where there's friction." },
      { icon: "🏥", title: "Healthcare, fitness, wellness", detail: "Hospitals, pharma, fitness chains, healthtech apps. Problem-solving for the body = wealth here." },
      { icon: "⚖️", title: "Legal practice & litigation", detail: "Lawyers, mediators, arbitration. Rahu loves the chess game of law." },
    ],
    avoid: "Avoiding conflict. Rahu in 6 is built TO compete — running away will make you ill, not rich.",
    energy: "Lawsuits, health issues if you overwork. Discipline in routine is the antidote.",
  },
  7: {
    theme: "Partnerships · Marriage · Foreign trade",
    headline: "Wealth flows through PARTNERS, especially foreign ones.",
    pillars: [
      { icon: "🤝", title: "Partnership businesses", detail: "Co-founder roles, joint ventures, agencies. Rahu in 7 needs a partner more than capital." },
      { icon: "🌍", title: "Foreign trade & import-export", detail: "Cross-border deals, export businesses, international consulting. Classical Rahu-7 signature." },
      { icon: "💍", title: "Marriage to wealthy / foreign partner", detail: "Often quietly upgrades the seeker's financial standing. Doesn't sound spiritual but Vedic texts say it." },
    ],
    avoid: "Going solo. You will plateau as a one-person business — find a complementary partner.",
    energy: "Marriage / partnership chosen in Rahu dasha often defines lifelong finances. Choose wisely.",
  },
  8: {
    theme: "Hidden · Occult · Inheritance · Sudden gains",
    headline: "Hidden, transformational, inheritance-based wealth — and the occult itself.",
    pillars: [
      { icon: "🔮", title: "Occult, astrology, research", detail: "Vedic astrology, tantra, healing therapies, parapsychology, deep research. Massive earners exist in this exact placement." },
      { icon: "🛢️", title: "Insurance, taxation, mining, oil", detail: "Industries dealing with hidden assets, risk, underground resources. Rahu in 8 mines wealth from the unseen." },
      { icon: "💎", title: "Inheritance / spouse's wealth", detail: "Sudden gains via family inheritance, in-laws, or windfalls. Often unexpected and outsized." },
    ],
    avoid: "Speculative gambling and shortcuts. Rahu-8 attracts scams disguised as 'opportunities'.",
    energy: "Volatile income — feast and famine cycles. Build a war chest in the feast years.",
  },
  9: {
    theme: "Bhagya · Higher learning · Foreign · Mentors",
    headline: "Luck arrives through teachers, foreign lands and big-picture ideas.",
    pillars: [
      { icon: "🎓", title: "Higher education & publishing", detail: "PhD-level work, professorship, publishing house, edu-companies. The 'gyan' route to wealth." },
      { icon: "✈️", title: "Travel, tourism, aviation", detail: "Tour operators, travel-tech, aviation, hospitality at scale. Movement = money for Rahu-9." },
      { icon: "🕉️", title: "Spiritual / religion-based businesses", detail: "Yoga retreats, wellness travel, spiritual content. Big audiences pay for transformative experiences." },
    ],
    avoid: "Abandoning ethics for shortcuts. Rahu-9 punishes loss of dharma severely.",
    energy: "Foreign travel and study abroad are extraordinarily lucky. Take the opportunities.",
  },
  10: {
    theme: "Career · Authority · Public image · Government",
    headline: "Career-driven wealth — high office, fame, government, top brands.",
    pillars: [
      { icon: "🏛️", title: "Politics, government, civil service", detail: "Public office, government tenders, regulator-facing businesses. Rahu in 10 climbs the authority ladder fast." },
      { icon: "💼", title: "Top corporate ranks", detail: "C-suite roles in MNCs, BFSI, tech. Rahu in 10 attracts the marquee logo on your CV." },
      { icon: "📰", title: "Media, brand-building, PR", detail: "Brand management, journalism, advertising, broadcasting. Public-image industries pay you handsomely." },
    ],
    avoid: "Cutting corners. Rahu-10 falls hardest when ethics slip — public reputation is the wealth.",
    energy: "Career rise tends to be SUDDEN. Be ready when the door opens — networks matter.",
  },
  11: {
    theme: "Gains · Big income · Networks · Friends",
    headline: "THE BEST POSSIBLE Rahu placement for wealth. You will earn at scale.",
    pillars: [
      { icon: "🌐", title: "Network-driven income", detail: "Network marketing, sales-team-based businesses, platforms, marketplaces, community-led startups." },
      { icon: "💰", title: "Multiple income streams", detail: "Don't depend on one — Rahu-11 multiplies money when you diversify. Aim for 5+ income lines by 40." },
      { icon: "🚀", title: "Scale, entrepreneurship, fame", detail: "Build something big. Rahu-11 is the placement of the founder, the influencer-entrepreneur, the empire-builder." },
    ],
    avoid: "Greed and never-feeling-content. Even at ₹100 crore Rahu-11 may whisper 'not enough'.",
    energy: "You have one of the most powerful wealth placements in the zodiac. Be grateful and disciplined.",
  },
  12: {
    theme: "Foreign · Loss · Spirituality · Hospitals · Ashrams",
    headline: "Foreign-earned wealth, spiritual industries, and HIDDEN expenses.",
    pillars: [
      { icon: "🌐", title: "Foreign settlement & overseas income", detail: "NRI work, remote-foreign-client services, expat assignments. Earn in dollars, live in rupees — Rahu-12 magic." },
      { icon: "🧘", title: "Spirituality, hospitals, ashrams, charity", detail: "Wellness retreats, ashram management, healing centres, hospital chains. Industries that 'remove' suffering." },
      { icon: "🛏️", title: "Hospitality (premium) & dream-state industries", detail: "Luxury hotels, sleep-tech, meditation apps, dream/lucid-dream coaching. Rahu loves 12th-house mystery." },
    ],
    avoid: "Hidden expenses, court cases, and unseen leakages eat Rahu-12 wealth. Audit your spending monthly.",
    energy: "Wealth flows in and out fast. Save aggressively when it flows in — the dry spells come.",
  },
};

const KETU_SHORT = {
  1: "You under-value yourself — outsource personal branding.",
  2: "You speak too little — your speech holds untapped wealth.",
  3: "You hesitate to hustle — courage in communication is your unlock.",
  4: "You feel uprooted from family — buy land to anchor.",
  5: "You doubt your creativity — your art is more valuable than you think.",
  6: "You avoid conflict — pick your fights and win them.",
  7: "You undervalue partnerships — find a complementary partner.",
  8: "You ignore hidden assets — claim what's rightfully yours.",
  9: "You doubt your luck — your blessings are bigger than you realise.",
  10: "You under-promote your career achievements — speak up about your wins.",
  11: "You shy away from networking — your network is your true asset.",
  12: "You over-spiritualise money — making money is also dharma.",
};

export default function RahuPlacementPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [date, setDate] = useState("");
  const [hour, setHour] = useState("");
  const [minute, setMinute] = useState("");
  const [place, setPlace] = useState("");
  const [timezone, setTimezone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  // place autocomplete
  const [showSug, setShowSug] = useState(false);
  const [sug, setSug] = useState([]);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef(null);
  const reqIdRef = useRef(0);
  const wrapRef = useRef(null);

  // One-shot prefill from signed-in user profile (shared hook)
  useBirthDetailsPrefill((bd) => {
    if (!bd?.date || !bd?.time || !bd?.place) return;
    const [hh, mm] = String(bd.time).split(":");
    setDate((prev) => prev || bd.date);
    setHour((prev) => prev !== "" ? prev : (hh || ""));
    setMinute((prev) => prev !== "" ? prev : (mm || ""));
    setPlace((prev) => prev || bd.place);
    setTimezone((prev) => prev || bd.timezone || "Asia/Kolkata");
  });

  useSavedReportLoader({
    expectedTool: "rahu",
    setForm: (bd) => {
      if (bd?.date) setDate(bd.date);
      if (bd?.time) { const [h, m] = bd.time.split(":"); setHour(h); setMinute(m); }
      if (bd?.place) setPlace(bd.place);
      if (bd?.timezone) setTimezone(bd.timezone);
    },
    setResult,
  });

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setShowSug(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const onPlace = (val) => {
    setPlace(val);
    setTimezone("");
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const t = val.trim();
    if (t.length < 2) { setSug([]); setSearching(false); setShowSug(t.length > 0); return; }
    setSearching(true);
    setShowSug(true);
    const id = ++reqIdRef.current;
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/places/search`, { params: { q: t } });
        if (id !== reqIdRef.current) return;
        setSug(Array.isArray(data) ? data : []);
      } catch (_) {
        if (id !== reqIdRef.current) return;
        setSug([]);
      } finally {
        if (id === reqIdRef.current) setSearching(false);
      }
    }, 250);
  };

  const pickSug = (s) => {
    setPlace(s.name);
    setTimezone(s.timezone);
    setShowSug(false);
    setSug([]);
  };

  const canSubmit = date && hour !== "" && minute !== "" && place && timezone;

  const onSubmit = async (e) => {
    e?.preventDefault();
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    try {
      const time = `${hour}:${minute}`;
      const { data } = await api.post("/rahu/compute", { date, time, place, timezone });
      setResult(data);
      setTimeout(() => document.getElementById("rh-result")?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not compute Rahu placement. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const guide = result ? RAHU_HOUSE_GUIDES[result.rahu.house] : null;

  return (
    <PageShell>
      <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-10 pt-10 sm:pt-16 pb-24" data-testid="rahu-placement-page">
        <div className="flex items-center gap-2 text-gold label-eyebrow">
          <Rocket size={14} weight="fill" /> Free · Rahu wealth-alignment
        </div>
        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-3 font-light tracking-tight leading-[1.1]">
          Rahu Placement <span className="italic text-gold">Calculator</span>
        </h1>
        <p className="text-muted text-sm sm:text-base mt-4 max-w-2xl font-light">
          Rahu — the karmic node of obsession, foreign winds and modern wealth — shows you the EXACT door to massive earning in this lifetime. Find your Rahu house and get a practical, house-by-house playbook for aligning with it.
        </p>

        <div className="grid lg:grid-cols-12 gap-8 mt-10">
          {/* FORM */}
          <form onSubmit={onSubmit} className="card-surface rounded-md p-5 sm:p-7 lg:col-span-5 self-start" data-testid="rh-form">
            <div className="flex items-center gap-2 label-eyebrow text-gold"><Sparkle size={14} weight="fill" /> Your birth details</div>
            <div className="mt-5 space-y-4">
              <div>
                <label className="label-eyebrow">Date of birth</label>
                <input type="date" required value={date} onChange={(e) => setDate(e.target.value)}
                  className="w-full mt-2 px-3 py-2.5 border border-soft rounded-md focus:border-gold outline-none text-sm bg-transparent"
                  data-testid="rh-date" />
              </div>
              <div>
                <label className="label-eyebrow">Time of birth</label>
                <div className="flex items-center gap-2 mt-2">
                  <select required value={hour} onChange={(e) => setHour(e.target.value)} className="flex-1 px-3 py-2.5 border border-soft rounded-md focus:border-gold outline-none text-sm bg-bg" data-testid="rh-hour">
                    <option value="">Hour</option>
                    {HOURS.map((h) => <option key={h} value={h}>{h}</option>)}
                  </select>
                  <span className="text-muted">:</span>
                  <select required value={minute} onChange={(e) => setMinute(e.target.value)} className="flex-1 px-3 py-2.5 border border-soft rounded-md focus:border-gold outline-none text-sm bg-bg" data-testid="rh-minute">
                    <option value="">Min</option>
                    {MINUTES.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                  <span className="text-muted text-xs">24-hr</span>
                </div>
              </div>
              <div ref={wrapRef} className="relative">
                <label className="label-eyebrow">Place of birth</label>
                <div className="relative mt-2">
                  <input
                    type="text" required placeholder="City — e.g. Mumbai"
                    value={place}
                    onChange={(e) => onPlace(e.target.value)}
                    autoComplete="off"
                    className="w-full px-3 py-2.5 pr-9 border border-soft rounded-md focus:border-gold outline-none text-sm"
                    data-testid="rh-place"
                  />
                  <MapPin size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted" />
                </div>
                {showSug && (
                  <div className="absolute top-full left-0 right-0 mt-1 max-h-52 overflow-y-auto card-surface rounded-md border border-soft z-20 shadow-lg">
                    {searching && (
                      <div className="px-3 py-2 text-xs text-muted flex items-center gap-2">
                        <span className="inline-block w-3 h-3 border-2 border-gold border-t-transparent rounded-full animate-spin" /> Searching…
                      </div>
                    )}
                    {!searching && place.trim().length >= 2 && sug.length === 0 && (
                      <div className="px-3 py-2 text-xs text-muted">No matches.</div>
                    )}
                    {sug.map((s, i) => (
                      <button
                        type="button" key={`${s.lat}-${s.lon}-${i}`}
                        onClick={() => pickSug(s)}
                        className="w-full text-left px-3 py-2 hover:bg-[#D4AF37]/10 transition-colors border-b border-soft/40 last:border-0"
                        data-testid={`rh-place-option-${i}`}
                      >
                        <div className="text-sm">{s.name}</div>
                        <div className="text-xs text-muted mt-0.5">{s.timezone}</div>
                      </button>
                    ))}
                  </div>
                )}
                {timezone && <p className="text-muted text-[11px] mt-1.5">Timezone: <span className="text-gold font-mono">{timezone}</span></p>}
              </div>
            </div>
            <button
              type="submit"
              disabled={!canSubmit || submitting}
              className="btn-primary rounded-full w-full mt-6 py-3 font-medium disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="rh-submit-btn"
            >
              <Rocket size={16} weight="fill" />
              {submitting ? "Finding your Rahu…" : "Find my Rahu placement"}
            </button>
          </form>

          {/* RESULT */}
          <div className="lg:col-span-7" id="rh-result">
            {!result ? (
              <div className="card-surface rounded-md p-8 sm:p-12 text-center h-full flex flex-col items-center justify-center gap-3" data-testid="rh-placeholder">
                <Compass size={40} weight="duotone" className="text-gold opacity-70" />
                <p className="font-serif-display text-2xl sm:text-3xl font-light">Your Rahu playbook will open here</p>
                <p className="text-muted text-sm max-w-md">
                  Enter your birth details and we'll show you exactly which industries, careers and life-themes Rahu has pre-wired you to earn massive wealth from.
                </p>
              </div>
            ) : (
              <div className="space-y-5 anim-fadeup" data-testid="rh-result-content">
                {/* Placement header */}
                <div className="card-surface rounded-md p-6 sm:p-8 border-2 border-amber-300" data-testid="rh-placement">
                  <div className="label-eyebrow text-gold">Your Rahu placement</div>
                  <div className="flex items-baseline gap-3 mt-2 flex-wrap">
                    <div className="font-serif-display text-3xl sm:text-4xl font-light">
                      Rahu in the <span className="text-gold italic">{ordinal(result.rahu.house)} house</span>
                    </div>
                    <div className="text-muted text-sm">· {result.rahu.rashi} · Nakshatra {result.rahu.nakshatra}</div>
                  </div>
                  <p className="font-serif-display text-xl sm:text-2xl mt-3 font-light text-[#3F2D1B] leading-snug">
                    {guide?.headline}
                  </p>
                  <div className="mt-2 text-xs text-muted">
                    Lagna: {result.lagna.rashi} · Ketu (opposite): {ordinal(result.ketu.house)} house · {result.ketu.rashi}
                  </div>
                  <div className="mt-4 px-3 py-2 rounded-md bg-[#FFF6E0]/60 text-xs text-[#3F2D1B]">
                    <span className="font-medium">Theme:</span> {guide?.theme}
                  </div>
                </div>

                {/* Premium: 3 pillars + Avoid/Energy + Ketu + Dasha */}
                <PaywallGate
                  tool="rahu"
                  toolLabel="Rahu Placement Playbook"
                  birthDetails={{ date, time: `${hour}:${minute}`, place, timezone }}
                  report={result}
                >
                  <div className="space-y-5">
                    {/* Wealth-alignment pillars */}
                    <div className="card-surface rounded-md p-5 sm:p-7" data-testid="rh-pillars">
                      <div className="label-eyebrow text-gold">How to practically align with your Rahu — 3 wealth pillars</div>
                      <div className="mt-4 space-y-4">
                        {guide?.pillars.map((p, i) => (
                          <div key={i} className="flex items-start gap-3 border-b border-soft last:border-0 pb-4 last:pb-0" data-testid={`rh-pillar-${i}`}>
                            <div className="text-3xl flex-shrink-0">{p.icon}</div>
                            <div>
                              <div className="font-medium text-sm sm:text-base text-[#3F2D1B]">{p.title}</div>
                              <p className="text-muted text-xs sm:text-sm mt-1 leading-relaxed">{p.detail}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Avoid + Energy */}
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="card-surface rounded-md p-5 border-l-4 border-rose-400" data-testid="rh-avoid">
                        <div className="flex items-center gap-2 label-eyebrow text-rose-700">
                          <Warning size={14} weight="fill" /> Avoid
                        </div>
                        <p className="text-sm text-[#3F2D1B] mt-2 leading-relaxed font-light">{guide?.avoid}</p>
                      </div>
                      <div className="card-surface rounded-md p-5 border-l-4 border-emerald-400" data-testid="rh-energy">
                        <div className="flex items-center gap-2 label-eyebrow text-emerald-700">
                          <Sparkle size={14} weight="fill" /> Energy / dasha tip
                        </div>
                        <p className="text-sm text-[#3F2D1B] mt-2 leading-relaxed font-light">{guide?.energy}</p>
                      </div>
                    </div>

                    {/* Ketu callout */}
                    <div className="card-surface rounded-md p-5 sm:p-7" data-testid="rh-ketu">
                      <div className="label-eyebrow text-gold">Your Ketu shadow ({ordinal(result.ketu.house)} house)</div>
                      <p className="font-serif-display text-lg sm:text-xl mt-2 font-light text-[#3F2D1B] leading-snug">
                        {KETU_SHORT[result.ketu.house]}
                      </p>
                      <p className="text-muted text-xs mt-2 font-light leading-relaxed">
                        Ketu sits opposite Rahu in your chart — it shows the area you naturally detach from, even though wealth karma is also stored there. Don't abandon it entirely.
                      </p>
                    </div>

                    {/* Dasha hint */}
                    {result.current_mahadasha && (
                      <div className="card-surface rounded-md p-5 sm:p-6 bg-[#FFF6E0]/40" data-testid="rh-dasha">
                        <div className="label-eyebrow text-gold">Your current dasha window</div>
                        <p className="text-sm mt-2 text-[#3F2D1B] font-light leading-relaxed">
                          You're currently in <span className="font-medium">{result.current_mahadasha.lord}</span> mahadasha
                          {result.current_antardasha && (
                            <> · <span className="font-medium">{result.current_antardasha.lord}</span> antardasha</>
                          )}
                          . The interplay of your Rahu-house theme with this dasha lord decides WHEN this wealth karma actually activates. An astrologer can pinpoint the exact months.
                        </p>
                      </div>
                    )}
                  </div>
                </PaywallGate>

                {/* CTA — Bhrigu Nadi specialist (reads Rahu placements karmically) */}
                <ToolAstrologerCta
                  requiredTechnique="Bhrigu Nandi Nadi"
                  pitch={`Decode the exact career windows & remedies for your Rahu placement — live, with a Bhrigu-Nadi specialist`}
                  ctaLabel="Talk to"
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </PageShell>
  );
}

function ordinal(n) {
  if (n === 1) return "1st";
  if (n === 2) return "2nd";
  if (n === 3) return "3rd";
  return `${n}th`;
}
